import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { tracks as tracksApi } from "../../lib/api";
import { useUI } from "../../lib/store";
import { Button } from "../shared/Button";
import { IconButton } from "../shared/IconButton";
import { Plus } from "../shared/Icon";
import { ContextMenu, CtxMenuItem } from "../shared/Menu";
import { StatusDot, statusLabel } from "../shared/StatusDot";
import { Modal } from "../shared/Modal";
import { cn } from "../../lib/cn";
import type { Track, TrackStatus } from "../../lib/types";
import { TrackEditor } from "./TrackEditor";
import { TrackDetail } from "./TrackDetail";

export function TracksView() {
  const projectId = useUI((s) => s.selectedProjectId);
  const selectedTrackId = useUI((s) => s.selectedTrackId);
  const setSelectedTrackId = useUI((s) => s.setSelectedTrackId);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Track | null>(null);
  const queryClient = useQueryClient();

  const { data: list = [] } = useQuery({
    queryKey: ["tracks", projectId],
    queryFn: () => tracksApi.list(projectId ?? undefined),
    refetchInterval: 5000,
  });

  const selected = list.find((t) => t.id === selectedTrackId) ?? null;

  return (
    <div className="flex flex-1 min-h-0">
      <aside className="w-[320px] border-r border-border-subtle overflow-y-auto">
        <div className="p-3 flex items-center justify-between">
          <div className="text-small text-text-tertiary uppercase tracking-wide">
            Tracks
          </div>
          <IconButton
            icon={<Plus />}
            label={projectId ? "New track" : "Select a project first"}
            onClick={() => setCreating(true)}
            disabled={!projectId}
          />
        </div>
        {list.length === 0 && (
          <div className="px-3 pb-4 text-small text-text-tertiary">
            {projectId
              ? "No tracks yet. Create one to plan multi-feature work."
              : "Select a project from the sidebar to view its tracks."}
          </div>
        )}
        <ul className="px-1">
          {list.map((t) => (
            <li key={t.id}>
              <TrackRow
                track={t}
                active={t.id === selectedTrackId}
                onClick={() => setSelectedTrackId(t.id)}
                onEdit={() => setEditing(t)}
              />
            </li>
          ))}
        </ul>
      </aside>
      <main className="flex-1 overflow-y-auto">
        {selected ? (
          <TrackDetail
            track={selected}
            onClose={() => setSelectedTrackId(null)}
            onEdit={() => setEditing(selected)}
          />
        ) : (
          <EmptyState
            hasTracks={list.length > 0}
            onCreate={() => setCreating(true)}
            disabled={!projectId}
          />
        )}
      </main>

      {creating && projectId && (
        <Modal
          onClose={() => setCreating(false)}
          title="New track"
          size="lg"
        >
          <TrackEditor
            projectId={projectId}
            onCancel={() => setCreating(false)}
            onCreated={(t) => {
              setCreating(false);
              setSelectedTrackId(t.id);
            }}
          />
        </Modal>
      )}

      {editing && (
        <Modal
          onClose={() => setEditing(null)}
          title="Edit track"
          size="lg"
        >
          <TrackEditor
            projectId={editing.project_id}
            track={editing}
            onCancel={() => setEditing(null)}
            onCreated={(t) => {
              setEditing(null);
              setSelectedTrackId(t.id);
              queryClient.invalidateQueries({ queryKey: ["tracks"] });
            }}
          />
        </Modal>
      )}
    </div>
  );
}

function TrackRow({
  track,
  active,
  onClick,
  onEdit,
}: {
  track: Track;
  active: boolean;
  onClick: () => void;
  onEdit: () => void;
}) {
  return (
    <ContextMenu
      items={
        <CtxMenuItem onSelect={onEdit}>
          Edit track
        </CtxMenuItem>
      }
    >
      <button
        onClick={onClick}
        className={cn(
          "w-full text-left px-3 py-2 rounded-md group transition-colors duration-fast",
          active ? "bg-bg-inset" : "hover:bg-bg-inset/60",
        )}
      >
        <div className="flex items-center gap-2">
          <StatusDot status={track.status as TrackStatus} />
          <span className="text-body text-text-primary truncate">{track.title}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-3 text-mono text-text-quaternary">
          <span>{statusLabel(track.status as TrackStatus)}</span>
        </div>
      </button>
    </ContextMenu>
  );
}

function EmptyState({
  hasTracks,
  onCreate,
  disabled,
}: {
  hasTracks: boolean;
  onCreate: () => void;
  disabled: boolean;
}) {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center">
        <div className="text-heading text-text-secondary mb-1">
          {hasTracks ? "Select a track" : "No tracks yet"}
        </div>
        <div className="text-small text-text-tertiary mb-3">
          {hasTracks
            ? "Pick one from the left to view milestones."
            : "Tracks plan multi-feature work with validated milestones."}
        </div>
        {!hasTracks && (
          <Button
            variant="primary"
            onClick={onCreate}
            disabled={disabled}
          >
            New track
          </Button>
        )}
      </div>
    </div>
  );
}
